import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { LoggingService } from './logging.service';
import { TimezoneStore } from '@app/core/state/timezone-store/timezone-store.service';

@Injectable({
    providedIn: 'root',
})
export class TimezoneConverterService {
    constructor(
        private loggingService: LoggingService,
        private timezoneStore: TimezoneStore
    ) {}

    /**
     * Converts a local date to UTC ISO string
     * No longer calls API - does local conversion
     */
    convertLocalToUtc(date: Date | string, timeZoneId?: string): Observable<string> {
        if (date instanceof Date) {
            return of(date.toISOString());
        }

        if (typeof date === 'string') {
            const trimmed = date.trim();
            if (trimmed && this.isTimestampWithOffset(trimmed)) {
                return of(trimmed);
            }
            // Convert string to Date and then to ISO
            const parsedDate = new Date(trimmed);
            return of(parsedDate.toISOString());
        }

        return of(new Date().toISOString());
    }

    /**
     * Converts UTC date to a localised display string.
     */
    convertUtcToLocal(date: Date | string, timeZoneId?: string): Observable<string> {
        return of(this.formatUtcToLocal(date, timeZoneId));
    }

    /**
     * Ensures the date is in UTC ISO string format
     * No longer calls API - does local conversion
     */
    ensureUtcIsoString(date: Date | string, timeZoneId?: string): Observable<string> {
        if (date instanceof Date) {
            return of(date.toISOString());
        }

        if (!date) {
            return of(new Date().toISOString());
        }

        const trimmed = date.trim();
        if (!trimmed) {
            return of(new Date().toISOString());
        }

        // If string already has timezone offset (Z or +/-HH:MM), it's already in UTC format
        if (this.isTimestampWithOffset(trimmed)) {
            return of(trimmed);
        }

        // If string has no timezone info, parse it and convert to ISO
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(trimmed)) {
            const parsedDate = new Date(trimmed);
            return of(parsedDate.toISOString());
        }

        if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(trimmed)) {
            // Space-separated format - convert to ISO format
            const isoFormat = trimmed.replace(' ', 'T');
            const parsedDate = new Date(isoFormat);
            return of(parsedDate.toISOString());
        }

        // Fallback: try to parse the date
        try {
            const parsedDate = new Date(trimmed);
            if (!isNaN(parsedDate.getTime())) {
                return of(parsedDate.toISOString());
            }
        } catch (error) {
            this.loggingService.warn('[TimezoneConverter] Failed to parse date:', trimmed);
        }

        // Last resort: return as-is
        return of(trimmed);
    }

    getBrowserTimeZone(): string | null {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
        } catch (error) {
            this.loggingService.warn('[TimezoneConverter] Failed to resolve browser timezone.', error);
            return null;
        }
    }

    /**
     * Convert a UTC value to a formatted local string synchronously.
     */
    formatUtcToLocal(date: Date | string, timeZoneId?: string): string {
        const parsed = this.parseDate(date);
        if (!parsed) {
            return '';
        }

        const fallbackZone = this.timezoneStore.currentTimeZone
            || this.getBrowserTimeZone()
            || 'UTC';
        const targetZone = timeZoneId || fallbackZone;

        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
                timeZone: targetZone,
            });

            const parts = formatter.formatToParts(parsed);
            const byType = (type: Intl.DateTimeFormatPartTypes) =>
                parts.find((p) => p.type === type)?.value ?? '';

            const year = byType('year');
            const month = byType('month');
            const day = byType('day');
            const hour = byType('hour');
            const minute = byType('minute');
            const second = byType('second');
            const period = (byType('dayPeriod') || '').toUpperCase();

            if (year && month && day && hour && minute && second && period) {
                return `${year}-${month}-${day} ${hour}:${minute}:${second} ${period}`;
            }

            return formatter.format(parsed);
        } catch (error) {
            this.loggingService.warn('[TimezoneConverter] Intl formatting failed.', error);
            return parsed.toLocaleString();
        }
    }

    private parseDate(value: Date | string): Date | null {
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : new Date(value.getTime());
        }

        if (typeof value !== 'string') {
            return null;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        const parsed = new Date(trimmed);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    private isTimestampWithOffset(value: string): boolean {
        return /[zZ]|[+-]\d{2}:\d{2}$/.test(value);
    }
}
